import {
  automatichingRuleDescription,
  setAutomatchingAlgorithm,
} from '@adornis/bh-matching-matches/api/auto-matching.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { GenderPreference } from '@adornis/bh-matching-user/entities/buddy-user.js';
import {
  GradeLevel,
  type MatchingHautnahMenteeUser,
  type MatchingHautnahMentorUser,
} from '../entities/matching-user.js';

// This function needs to match the type signature expected by setAutomatchingAlgorithm
// This function has types specific to MatchingHautnah entities
export const _autoMatchUsers = (
  mentors: MatchingHautnahMentorUser[],
  mentees: MatchingHautnahMenteeUser[],
  existingMatches: Match[] = [],
) => {
  const newMatches: Match[] = [];

  // Group mentors by timeframeID
  const mentorsByTimeframe = new Map<string, MatchingHautnahMentorUser[]>();
  for (const mentor of mentors ?? []) {
    if (!mentor.has('timeframeID')) continue;

    const timeframeID = mentor.timeframeID as string;
    if (!mentorsByTimeframe.has(timeframeID)) {
      mentorsByTimeframe.set(timeframeID, []);
    }
    mentorsByTimeframe.get(timeframeID)?.push(mentor);
  }

  // sort mentees by GradeLevel (higher = more important)
  const gradeLevelWeight = {
    [GradeLevel.Q4]: 4,
    [GradeLevel.Q2]: 3,
    [GradeLevel.E2]: 2,
    [GradeLevel.GRADE_10_OR_LOWER]: 1,
    [GradeLevel.GRADUATED]: 0,
  };
  const gl = (user: MatchingHautnahMenteeUser) => {
    if (!user.gradeLevel) return 0;
    return gradeLevelWeight[user.gradeLevel];
  };
  mentees.sort((a, b) => gl(b) - gl(a));

  // Process each mentee
  for (const mentee of mentees ?? []) {
    // Skip mentees without timeframeID
    if (!mentee.has('timeframeID')) continue;

    const menteeTimeframeID = mentee.timeframeID as string;
    const timeframeMentors = mentorsByTimeframe.get(menteeTimeframeID) ?? [];

    // Filter mentors by matching criteria
    const filteredMentors = timeframeMentors
      .filter(mentor => {
        // Check gender preference
        if (
          mentee.has('genderPreference') &&
          mentee.has('gender') &&
          mentor.has('gender') &&
          (mentee.genderPreference === GenderPreference.SAME_SEX ||
            mentor.genderPreference === GenderPreference.SAME_SEX) &&
          mentee.gender !== mentor.gender
        ) {
          return false;
        }

        // check day availibity
        if (!mentee.availableDates?.some(day => mentor.availableDates?.includes(day))) {
          return false;
        }

        // check for primary or secondary study interests
        if (
          !mentor.studyPrograms?.includes(mentee.primaryStudyInterest!) &&
          !mentee.additionalStudyInterests?.some(interest => mentor.studyPrograms?.includes(interest))
        ) {
          return false;
        }

        return true;
      })
      .toSorted((a, b) => {
        //sort by studiengang interest
        let scorea = 0;
        let scoreb = 0;
        if (a.studyPrograms?.includes(mentee.primaryStudyInterest!)) {
          scorea += 10;
        }

        if (b.studyPrograms?.includes(mentee.primaryStudyInterest!)) {
          scoreb += 10;
        }

        // sort by group size preference
        if (!!a.numberOfMentees && a.numberOfMentees === mentee.numberOfFellowMentees) {
          scorea += 5;
        }

        if (!!b.numberOfMentees && b.numberOfMentees === mentee.numberOfFellowMentees) {
          scoreb += 5;
        }

        for (const interest of mentee.additionalStudyInterests ?? []) {
          if (a.studyPrograms?.includes(interest)) {
            scorea += 2;
          }

          if (b.studyPrograms?.includes(interest)) {
            scoreb += 2;
          }
        }

        return scoreb - scorea;
      });

    // Check for mentor's maximum daily student limit
    // Calculate existing matches for each mentor in this timeframe
    const mentorMatchCounts = new Map<string, number>();

    // Count existing matches
    for (const match of existingMatches) {
      if (match.mentorID) {
        mentorMatchCounts.set(match.mentorID, (mentorMatchCounts.get(match.mentorID) ?? 0) + 1);
      }
    }

    // Count new matches that were already created in this run
    for (const match of newMatches) {
      if (match.mentorID) {
        mentorMatchCounts.set(match.mentorID, (mentorMatchCounts.get(match.mentorID) ?? 0) + 1);
      }
    }

    // Filter out mentors who have reached their maximum student limit
    const availableMentors = filteredMentors.filter(mentor => {
      if (!mentor.numberOfMentees) return true;
      const currentMatchCount = mentorMatchCounts.get(mentor._id as string) ?? 0;
      return currentMatchCount < mentor.numberOfMentees;
    });

    // Sort mentors by current match count (prefer mentors with fewer matches)
    availableMentors.sort((a, b) => {
      const countA = mentorMatchCounts.get(a._id as string) ?? 0;
      const countB = mentorMatchCounts.get(b._id as string) ?? 0;
      return countA - countB;
    });

    // Create match with the most suitable mentor (if available)
    const bestMentor = availableMentors[0];
    if (bestMentor) {
      const match = new Match({
        mentee,
        menteeID: mentee._id,
        mentor: bestMentor,
        mentorID: bestMentor._id,
        timeframeID: menteeTimeframeID, // Ensure the match is associated with the timeframe
      });
      newMatches.push(match);

      // Update match count for this mentor
      mentorMatchCounts.set(bestMentor._id as string, (mentorMatchCounts.get(bestMentor._id as string) ?? 0) + 1);
    }
  }

  return newMatches;
};

setAutomatchingAlgorithm((mentors, mentees, existingMatches = []) => {
  return _autoMatchUsers(
    mentors as unknown as MatchingHautnahMentorUser[],
    mentees as unknown as MatchingHautnahMenteeUser[],
    existingMatches,
  );
});

automatichingRuleDescription.text = `
Das Auto-Matching wird anhand der folgenden Kriterien durchgeführt:

- Geschlecht
- Tag des Matching Days (aus dem Anmeldeformular)
- Studienfach
- Schüler der höheren Klassen bevorzugen (Q4>Q2>E2>10/Niedrigere Klasse)
- Studenten bekommen max. die Anzahl Schüler pro Tag gemacht, die sie angegeben haben.
`;
